import type { Workflow, ScanRequest } from '@/shared/types'
import type { WorkflowExecutionEntry } from '@/features/orchestration/types'
import { API_URL, createHeaders } from './base'

export async function getWorkflows(status?: string, workflowType?: string): Promise<Workflow[]> {
  const params = new URLSearchParams()
  if (status) params.append('status', status)
  if (workflowType) params.append('workflow_type', workflowType)

  const url = `${API_URL}/workflows${params.toString() ? `?${params}` : ''}`
  const response = await fetch(url, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch workflows')
  return response.json()
}

export async function getWorkflow(id: number): Promise<Workflow> {
  const response = await fetch(`${API_URL}/workflows/${id}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch workflow')
  return response.json()
}

export async function getPendingWorkflowCount(): Promise<number> {
  const response = await fetch(`${API_URL}/workflows/pending/count`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch pending count')
  const data = await response.json()
  return data.count
}

export async function approveWorkflow(id: number, notes?: string): Promise<Workflow> {
  const response = await fetch(`${API_URL}/workflows/${id}/approve`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ approved: true, notes }),
  })
  if (!response.ok) throw new Error('Failed to approve workflow')
  return response.json()
}

export async function rejectWorkflow(id: number, notes?: string): Promise<Workflow> {
  const response = await fetch(`${API_URL}/workflows/${id}/approve`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ approved: false, notes }),
  })
  if (!response.ok) throw new Error('Failed to reject workflow')
  return response.json()
}

export async function updateWorkflow(id: number, updates: Record<string, unknown>): Promise<Workflow> {
  const response = await fetch(`${API_URL}/workflows/${id}`, {
    method: 'PATCH',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(updates),
  })
  if (!response.ok) throw new Error('Failed to update workflow')
  return response.json()
}

export async function deleteWorkflow(id: number): Promise<void> {
  const response = await fetch(`${API_URL}/workflows/${id}`, {
    method: 'DELETE',
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to delete workflow')
}

export async function retryWorkflow(id: number): Promise<Workflow> {
  const response = await fetch(`${API_URL}/workflows/${id}/retry`, {
    method: 'POST',
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to retry workflow')
  return response.json()
}

export async function unlockWorkflow(id: number): Promise<Workflow> {
  const response = await fetch(`${API_URL}/workflows/${id}/unlock`, {
    method: 'POST',
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to unlock workflow')
  return response.json()
}

export async function auditWorkflow(id: number): Promise<{ workflow_id: number; audit_status: string; audit_result: { verified: boolean; confidence: number; risk_level: string; issues: string[]; notes: string } }> {
  const response = await fetch(`${API_URL}/workflows/${id}/audit`, {
    method: 'POST',
    headers: createHeaders(),
  })
  if (!response.ok) {
    const data = await response.json()
    throw new Error(data.detail || 'Audit failed')
  }
  return response.json()
}

export async function triggerScan(request: ScanRequest): Promise<Workflow[]> {
  const response = await fetch(`${API_URL}/workflows/scan`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(request),
  })
  if (!response.ok) throw new Error('Failed to trigger scan')
  return response.json()
}

export async function executeWorkflow(id: number, dryRun: boolean = false): Promise<{
  success: boolean
  message: string
  files_modified: string[]
  error?: string
  diff_summary?: string
  workflow_id: number
  status: string
}> {
  const response = await fetch(`${API_URL}/workflows/${id}/execute?dry_run=${dryRun}`, {
    method: 'POST',
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to execute workflow')
  return response.json()
}

export async function executeWorkflowWithClaude(id: number, dryRun: boolean = false): Promise<{
  success: boolean
  message: string
  stdout?: string
  stderr?: string
  error?: string
  workflow_id: number
  status: string
  executor: string
  execution_log: Array<{ timestamp: string; action: string; details: string; data?: unknown }>
}> {
  const response = await fetch(`${API_URL}/workflows/${id}/execute/claude?dry_run=${dryRun}`, {
    method: 'POST',
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to execute workflow with Claude')
  return response.json()
}

export async function executeWorkflowWithGemini(id: number, dryRun: boolean = false): Promise<{
  success: boolean
  message: string
  files_modified: string[]
  error?: string
  workflow_id: number
  status: string
}> {
  const response = await fetch(`${API_URL}/workflows/${id}/execute/gemini-cli?dry_run=${dryRun}`, {
    method: 'POST',
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to execute workflow with Gemini')
  return response.json()
}

export async function executeWorkflowWithCodex(id: number, dryRun: boolean = false): Promise<{
  success: boolean
  message: string
  files_modified: string[]
  error?: string
  workflow_id: number
  status: string
}> {
  const response = await fetch(`${API_URL}/workflows/${id}/execute/codex?dry_run=${dryRun}`, {
    method: 'POST',
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to execute workflow with Codex')
  return response.json()
}

export async function executeWorkflowWithExecutor(
  id: number,
  executor: string,
  dryRun: boolean = false
): Promise<{
  success: boolean
  message: string
  files_modified?: string[]
  error?: string
  workflow_id: number
  status: string
  executor?: string
  execution_log?: Array<{ timestamp: string; action: string; details: string; data?: unknown }>
}> {
  switch (executor) {
    case 'claude':
      return executeWorkflowWithClaude(id, dryRun)
    case 'gemini':
      return executeWorkflowWithGemini(id, dryRun)
    case 'codex':
      return executeWorkflowWithCodex(id, dryRun)
    default:
      throw new Error(`Unknown executor: ${executor}`)
  }
}

export async function getWorkflowEntries(workflowId: number): Promise<WorkflowExecutionEntry[]> {
  const response = await fetch(`${API_URL}/workflows/${workflowId}/entries`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch workflow entries')
  return response.json()
}

export async function postWorkflowGuidance(workflowId: number, comment: string): Promise<void> {
  const response = await fetch(`${API_URL}/workflows/${workflowId}/entries`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      action: 'user_guidance',
      agent: 'user',
      summary: `User guidance: ${comment.slice(0, 80)}`,
      details: { guidance: comment },
    }),
  })
  if (!response.ok) throw new Error(`Failed to post guidance: ${response.status}`)
}

export async function createDraftWorkflow(data: {
  title: string
  description?: string
  assigned_agent?: string
  repository_path?: string
  workflow_type?: string
  priority?: string
  goal_id?: number
  goal?: string
  target_files?: string[]
}): Promise<Workflow> {
  const response = await fetch(`${API_URL}/workflows`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(data)
  })
  if (!response.ok) throw new Error('Failed to create draft workflow')
  return response.json()
}

export async function enrichWorkflow(workflowId: number): Promise<{ status: string; workflow_id: number }> {
  const response = await fetch(`${API_URL}/workflows/${workflowId}/enrich`, {
    method: 'POST',
    headers: createHeaders()
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to trigger enrichment' }))
    throw new Error(error.detail || 'Failed to trigger enrichment')
  }
  return response.json()
}
